/**
 * Runtime configuration (11 §11).
 *
 * Vite inlines every `VITE_*` reference at build time, which would make each image specific to one
 * environment and break promotion by digest — the exact bits approved in Staging have to be the ones
 * that reach Production. So the image ships with no configuration: the container's entrypoint writes a
 * `/config.js` from its environment, the document loads it before the bundle, and the app reads it
 * here. The build-time value stays as the fallback so `pnpm dev` and the tests keep working from the
 * root `.env` with nothing else to set up.
 */

/** The keys a portal may read at runtime. A typo fails to compile instead of silently reading nothing. */
export type RuntimeConfigKey =
  | 'VITE_CLERK_PUBLISHABLE_KEY'
  | 'VITE_AUTHZ_BACKEND'
  | 'VITE_AUTHZ_API_URL'
  | 'VITE_GOOGLE_MAPS_EMBED_KEY';

declare global {
  interface Window {
    /** Written by the container entrypoint; absent in `pnpm dev` and in tests. */
    __TMR_CONFIG__?: Partial<Record<RuntimeConfigKey, string>>;
  }
}

/**
 * Reads one configuration value: the container's value wins, the build-time one fills in.
 *
 * An empty string counts as "not set for this environment" rather than "set to empty", so a partially
 * filled `/config.js` never blanks out a value the local `.env` does provide.
 */
export function runtimeConfig(key: RuntimeConfigKey): string | undefined {
  const injected = typeof window === 'undefined' ? undefined : window.__TMR_CONFIG__?.[key];
  if (injected !== undefined && injected !== '') return injected;

  const built = (import.meta.env as Record<string, string | undefined>)[key];
  return built === '' ? undefined : built;
}
